import { Popover } from '@headlessui/react';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import NotificationToast from './NotificationToast';
import Button from '../buttons/Button';
import { useNotifications } from '../../hooks/notifications/useNotifications';
import { ROUTE_ARENA_FIELD_RESERVATION } from '../../router/constants';
import { getStringOrNull } from '../../utils/getHelpers';
import { useUnreadNotifications } from '../../hooks/notifications/useUnreadNotifications';
import { markAsReadNotifications } from '../../api/notifications';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';
import { AppNotification } from '../../types/notifications';
import dayjs from 'dayjs';
import { Player } from '../../types/players';
import PlayerDetailsModal from '../modals/PlayerDetailsModal';

type TProps = {
  containerClassName?: string;
};

const NotificationsContainer = ({ containerClassName }: TProps) => {
  const [clickedPlayer, setClickedPlayer] = useState<Player | null>(null);

  const { notifications, getNotifications, loadMore, error, hasNext, loading } =
    useNotifications();
  const {
    unreadNotificationsCount,
    clearUnreadNotificationsCount,
    setUnreadNotificationsCount,
  } = useUnreadNotifications();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const popoverClassName = useMemo(() => {
    let className = 'relative';
    if (containerClassName) {
      className += ` ${containerClassName}`;
    }
    return className;
  }, [containerClassName]);

  const readNotifications = useCallback(
    async (notificationIds?: number[]) => {
      if (unreadNotificationsCount > 0) {
        await markAsReadNotifications(notificationIds);
        await getNotifications();

        if (notificationIds && notificationIds.length > 0) {
          setUnreadNotificationsCount(-notificationIds.length);
        } else {
          clearUnreadNotificationsCount();
        }
      }
    },
    [
      unreadNotificationsCount,
      clearUnreadNotificationsCount,
      getNotifications,
      setUnreadNotificationsCount,
    ]
  );

  const onNotificationClick = useCallback(
    async (close: () => void, notification: AppNotification) => {
      close();

      if (notification.reservation) {
        const { reservation } = notification;

        if (
          reservation.isRecurring &&
          reservation.recurringEndDate &&
          reservation.recurringRepeatCount
        ) {
          const date = dayjs(reservation.recurringEndDate)
            .subtract(reservation.recurringRepeatCount - 1, 'weeks')
            .toISOString();

          navigate(
            `${generatePath(ROUTE_ARENA_FIELD_RESERVATION, {
              id: getStringOrNull(reservation?.field.complexId),
              fieldId: getStringOrNull(reservation?.field.id),
            })}?date=${date}`
          );
        } else {
          navigate(
            `${generatePath(ROUTE_ARENA_FIELD_RESERVATION, {
              id: getStringOrNull(reservation?.field.complexId),
              fieldId: getStringOrNull(reservation?.field.id),
            })}?date=${reservation?.date}`
          );
        }
      }

      if (notification.status !== 'RECEIVED') {
        await readNotifications([notification.id]);
      }
    },
    [navigate, readNotifications]
  );

  const renderNotifications = useCallback(
    (close: () => void) => {
      if (notifications.length === 0) {
        return (
          <p className="text-white text-center p-3">
            {t(COMMON_TRANSLATE_KEYS.NO_NOTIFICATIONS)}
          </p>
        );
      }

      return notifications.map((notification) => {
        const { title, body, status } = notification;

        let className = 'p-2 rounded-md border cursor-pointer';

        if (['PENDING', 'SENT'].includes(status)) {
          className += ' border-dividerGray bg-backGroundGray';
        } else {
          className += ' border-dividerGray';
        }

        return (
          <NotificationToast
            key={notification.id}
            title={title}
            body={body}
            notificationData={notification}
            className={className}
            onClick={async () => await onNotificationClick(close, notification)}
            onPlayerClick={(e, player: Player) => {
              e.preventDefault();
              e.stopPropagation();

              setClickedPlayer(player);
              close();
            }}
          />
        );
      });
    },
    [notifications, t, onNotificationClick]
  );

  return (
    <>
      <Popover className={popoverClassName}>
        <Popover.Button className="flex w-8">
          <img
            src="/icons/bell.svg"
            alt="Notification Bell"
            className="w-8 h-8 mr-4"
          />

          {unreadNotificationsCount > 0 &&
            Notification.permission === 'granted' && (
              <span className="rounded-full bg-cancel p-[0.6rem] text-white text-xxs w-4 h-4 flex items-center justify-center absolute top-0 right-0 mr-3">
                {unreadNotificationsCount > 10
                  ? '10+'
                  : `${unreadNotificationsCount}`}
              </span>
            )}
        </Popover.Button>
        <Popover.Panel
          className={`absolute top-8 rounded-md -left-[150%] flex flex-col border-2 ${
            notifications.length > 0
              ? 'border-additionalBlack'
              : 'border-dividerGray'
          } bg-mainBlack w-[20rem] z-[1001] overflow-auto max-h-[27rem]`}
          // onMouseLeave={readNotifications}
        >
          {({ close }) => (
            <>
              {Notification.permission !== 'granted' && (
                <p className="text-white text-center p-3 border border-red-900">
                  {t(COMMON_TRANSLATE_KEYS.NOTIFICATIONS_NOT_ALLOWED_TEXT)}
                </p>
              )}

              {error ? (
                <p className="text-white text-center p-3">{error}</p>
              ) : (
                renderNotifications(close)
              )}
              {hasNext && (
                <div className="flex flex-row justify-center p-4 bg-mainBlack">
                  <Button
                    isDisabled={loading}
                    onClick={() => loadMore()}
                    width={15}
                    className="border border-additionalGray"
                  >
                    {t(COMMON_TRANSLATE_KEYS.LOAD_MORE)}
                  </Button>
                </div>
              )}
            </>
          )}
        </Popover.Panel>
      </Popover>

      {clickedPlayer && (
        <PlayerDetailsModal
          open={!!clickedPlayer}
          close={() => setClickedPlayer(null)}
          player={clickedPlayer}
        />
      )}
    </>
  );
};

export default NotificationsContainer;
